//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { themr, type ThemeType } from 'react-css-themr';
import WorkingDaysSelectorWithOffset from 'common/entity/components/dayShift/WorkingDaysSelectorWithOffset';
import slotSelectorModalFactory from 'common/entity/components/slot/SlotsSelectorModalFactory';
import { compose } from 'recompose';

import { translate, type TFunction } from 'react-i18next';
import theme from './WorkingDaysSelectorWithOffsetModal.scss';
import { DayShift } from 'common/models/internal/DayShift';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import moment from 'moment-timezone';
import { SlotType } from 'common/enums/SlotEnum';
import { BreakShift } from 'common/models/internal/BreakShift';
import ArrayUtil from 'common/utils/ArrayUtil';
import { AddressLatLng } from 'common/models/internal/AddressLatLng';
import DateTimeUtils, { CustomDateFormats } from 'common/utils/DateTimeUtils';

export const WorkingDaysSelectorModal = slotSelectorModalFactory({
    SlotSelectorSection: WorkingDaysSelectorWithOffset
});

export const deriveSlotType = function (
    allSlots: Array<DayShift>
): $Values<typeof SlotType> {
    if (allSlots.length > 7) {
        throw new Error(`Slots length higher than 7: ${allSlots.length}`);
    }
    if (allSlots.length === 0) {
        //Default
        return SlotType.EVERYDAY;
    }
    if (allSlots.length < 7) {
        return SlotType.CUSTOM;
    }
    const [firstDay, ...otherDays] = allSlots;
    for (const day of otherDays) {
        if (day) {
            const {
                slot: daySlot,
                breaks: dayBreaks,
                startLocation,
                endLocation
            } = day;
            const {
                slot: firstDaySlot,
                breaks: firstDayBreaks,
                startLocation: firstStartLocation,
                endLocation: firstEndLocation
            } = firstDay;

            let isEqual =
                daySlot.equals(firstDaySlot) &&
                dayBreaks.equals(firstDayBreaks);
            isEqual =
                startLocation && firstStartLocation
                    ? startLocation.equals(firstStartLocation)
                    : isEqual;
            isEqual =
                endLocation && firstEndLocation
                    ? endLocation.equals(firstEndLocation)
                    : isEqual;
            if (!isEqual) {
                return SlotType.CUSTOM;
            }
        }
    }
    return SlotType.EVERYDAY;
};

type OwnProps = {
    className?: string,
    theme?: ThemeType,
    defaultWorkingDays: Array<DayShift>,
    onDismiss: (?boolean) => void,
    onWorkingDaysApply: (Array<DayShift>) => void,
    startLocationPlaceholder: string,
    endLocationPlaceholder: string
};

type Props = {
    ...OwnProps,
    theme: ThemeType,
    t: TFunction
};

type State = {
    showBreaksDialog: boolean,
    breakSlotFor?: $Values<typeof DayOfWeekType>
};
class WorkingDaysSelectorWithOffsetModal extends React.PureComponent<
    Props,
    State
> {
    state = {
        showBreaksDialog: false
    };

    copySlotToAll = ({
        timeSlots: workingDays,
        slotFor
    }: {
        timeSlots: Array<DayShift>,
        slotFor: string
    }) => {
        const sourceTimeslot = workingDays.find(
            workingDay => workingDay.dayOfWeek === slotFor
        );
        const updatedWorkingDays = workingDays.map(workingDay => {
            return workingDay
                .setSlot(sourceTimeslot.slot)
                .setBreaks(sourceTimeslot.breaks)
                .setStartLocation(sourceTimeslot.startLocation)
                .setEndLocation(sourceTimeslot.endLocation)
                .setEndDayOffset(sourceTimeslot.endDayOffset);
        });
        return updatedWorkingDays;
    };

    getUpdatedTimeSlots({
        timeSlots: workingDays,
        slotFor,
        start,
        end,
        breaks,
        startLocation,
        endLocation,
        deleteStartLocation,
        deleteEndLocation,
        endDayOffset
    }: {
        timeSlots: Array<DayShift>,
        slotFor: string,
        end: moment,
        start: moment,
        breaks: Array<BreakShift>,
        startLocation?: AddressLatLng,
        endLocation?: AddressLatLng,
        deleteStartLocation: boolean,
        deleteEndLocation: boolean,
        endDayOffset: number
    }) {
        const workingDayIndex = workingDays.findIndex(
            workingDay => workingDay.dayOfWeek === slotFor
        );
        let updatedWorkingDay = workingDays[workingDayIndex];
        const { slot: slotToBeUpdated } = updatedWorkingDay;
        if (breaks) {
            updatedWorkingDay = updatedWorkingDay.setBreaks(
                Immutable.List(breaks)
            );
            return ArrayUtil.insertItemAtIndex({
                array: workingDays,
                index: workingDayIndex,
                item: updatedWorkingDay
            });
        }
        if (deleteStartLocation || startLocation) {
            updatedWorkingDay = updatedWorkingDay.setStartLocation(
                deleteStartLocation ? null : startLocation
            );
        }
        if (deleteEndLocation || endLocation) {
            updatedWorkingDay = updatedWorkingDay.setEndLocation(
                deleteEndLocation ? null : endLocation
            );
        }
        if (start && end) {
            const updatedSlot = slotToBeUpdated
                .setStart(
                    DateTimeUtils.customFormat(
                        start,
                        CustomDateFormats.API_TIME
                    )
                )
                .setEnd(
                    DateTimeUtils.customFormat(end, CustomDateFormats.API_TIME)
                )
                .setEndDayOffset(updatedWorkingDay.endDayOffset);
            updatedWorkingDay = updatedWorkingDay.setSlot(updatedSlot);
        }
        updatedWorkingDay = updatedWorkingDay.setEndDayOffset(endDayOffset);
        return ArrayUtil.insertItemAtIndex({
            array: workingDays,
            index: workingDayIndex,
            item: updatedWorkingDay
        });
    }

    deriveSelectedDays(defaultSlots: ?Array<DayShift>) {
        if (!defaultSlots || defaultSlots.length === 0) {
            return Object.values(DayOfWeekType);
        }
        return defaultSlots.map(slot => slot.dayOfWeek);
    }

    validateTimeSlot = workingDay => {
        return !workingDay.validateBreaksWithSlot();
    };

    createDefaultSlot = () => {
        return {
            start: '08:00',
            end: '18:00'
        };
    };

    createInitialWorkingDays = (defaultWorkingDays?: ?Array<DayShift>) => {
        const slots = [];
        Object.values(DayOfWeekType).forEach(day => {
            const existingDayShift =
                defaultWorkingDays &&
                defaultWorkingDays.find(dayShift => dayShift.dayOfWeek === day);
            let newSlot = DayShift.fromJS({
                dayOfWeek: day,
                breaks: [],
                slot: this.createDefaultSlot(),
                endDayOffset: 0
            });
            if (existingDayShift) {
                newSlot = newSlot
                    .setBreaks(existingDayShift.breaks)
                    .setSlot(existingDayShift.slot)
                    .setStartLocation(existingDayShift.startLocation)
                    .setEndLocation(existingDayShift.endLocation)
                    .setEndDayOffset(existingDayShift.endDayOffset);
            }
            slots.push(newSlot);
        });
        return slots;
    };

    getIsTimeSlotEmpty(timeSlot) {
        return !timeSlot.slot;
    }

    onAddBreakClick = ({ slotFor }: { slotFor: string }) => {
        this.setState(({ showBreaksDialog }) => {
            return {
                showBreaksDialog: !showBreaksDialog,
                breakSlotFor: slotFor
            };
        });
    };

    closeBreaksDialog = () => {
        this.setState({
            showBreaksDialog: false,
            breakSlotFor: null
        });
    };

    render() {
        const {
            className,
            theme,
            defaultWorkingDays,
            onDismiss,
            onWorkingDaysApply,
            t,
            startLocationPlaceholder,
            endLocationPlaceholder
        } = this.props;
        const { showBreaksDialog, breakSlotFor } = this.state;
        return (
            <WorkingDaysSelectorModal
                startLocationPlaceholder={startLocationPlaceholder}
                endLocationPlaceholder={endLocationPlaceholder}
                theme={theme}
                className={className}
                title={t('riderForm.chooseWorkingDays')}
                onSlotApply={onWorkingDaysApply}
                closeBreaksDialog={this.closeBreaksDialog}
                onDismiss={onDismiss}
                defaultSlots={defaultWorkingDays}
                validateTimeSlot={this.validateTimeSlot}
                copySlotToAll={this.copySlotToAll}
                getUpdatedTimeSlots={this.getUpdatedTimeSlots}
                deriveSlotType={deriveSlotType}
                createDefaultSlot={this.createDefaultSlot}
                createInitialSlots={this.createInitialWorkingDays}
                deriveSelectedDays={this.deriveSelectedDays}
                getIsTimeSlotEmpty={this.getIsTimeSlotEmpty}
                showBreaksDialog={showBreaksDialog}
                breakSlotFor={breakSlotFor}
                onAddBreakClick={this.onAddBreakClick}
            />
        );
    }
}

export default (compose(
    translate('entityManagement'),
    themr('WorkingDaysSelectorWithOffsetModal', theme)
)(WorkingDaysSelectorWithOffsetModal): React.AbstractComponent<OwnProps>);
